import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import Sidebar from './Sidebar';

// 1. We define exactly what this component accepts
interface AppLayoutProps {
  children: React.ReactNode;
  currentView: string;
  onChangeView: (view: string) => void;
  isDarkMode: boolean;
  toggleTheme: () => void;
  showSidebar?: boolean;
}

// 2. We apply the type directly to the props (removing React.FC)
const AppLayout = ({ 
  children, 
  currentView, 
  onChangeView, 
  isDarkMode, 
  toggleTheme,
  showSidebar = true 
}: AppLayoutProps) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const handleMobileNav = (view: string) => {
    onChangeView(view);
    setSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-zinc-50 dark:bg-zinc-950 transition-colors duration-200">
      
      {/* MOBILE TOP BAR */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 p-4 flex justify-between items-center h-16 shadow-sm">
        <span className="font-bold text-lg text-zinc-900 dark:text-white">
          TOLO News OS
        </span>
        <button 
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          className="p-2 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
        >
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* SIDEBAR DRAWER */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800 transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${showSidebar ? 'lg:translate-x-0 lg:static lg:block' : 'lg:hidden'}
        `}
      >
        <Sidebar 
          currentView={currentView}
          onChangeView={handleMobileNav}
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
        />
      </aside>

      {/* MOBILE OVERLAY */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* MAIN CONTENT */}
      <main className="flex-1 w-full overflow-y-auto pt-16 lg:pt-0 h-screen relative">
        {children}
      </main>
      
    </div>
  );
};

export default AppLayout;